/**
 * The reflex games: reaction time, click speed, aim training, colour matching.
 *
 * All four are timing sensitive, so they share two rules. Timing always comes
 * from `performance.now()` rather than from counting frames, because a frame is
 * 16ms on one device and 8ms on another and a reaction timer that reports the
 * refresh rate is worthless. And every one of them is playable by touch: the
 * hit targets are pointer driven, never mouse driven.
 */
import { byLevel } from '@arena/games';

import { Lifecycle, fitCanvas, h, randomOf, tone } from '../kit.js';
import type { GameDef } from '../types.js';

function pill(label: string, value: string): [HTMLElement, HTMLElement] {
  const box = h('div', 'ac-pill');
  const strong = h('strong', '', value);
  box.append(h('span', '', label), strong);
  return [box, strong];
}

// ------------------------------------------------------------ reaction time

export const reactionTime: GameDef = {
  id: 'reaction-time',
  title: 'Temps de réaction',
  blurb: 'Clique des que le carre devient vert.',
  category: 'Réflexe',
  difficulty: 'Facile',
  icon: '⚡',
  unit: 'ms',
  higherIsBetter: false,
  how: 'Attends le vert puis clique le plus vite possible. Cliquer trop tot annule la manche.',
  mount(root, ctx) {
    const life = new Lifecycle();

    const zone = h('div');
    zone.style.cssText =
      'width:100%;max-width:560px;height:300px;border-radius:16px;display:grid;place-items:center;' +
      'cursor:pointer;user-select:none;font-size:22px;font-weight:800;text-align:center;padding:20px;' +
      'transition:background 0.08s linear';

    const note = h('p', 'ac-note', 'Cinq manches. On garde ta meilleure.');
    const [roundBox, roundValue] = pill('Manche', '1 / 5');
    const [lastBox, lastValue] = pill('Derniere', '--');
    const [avgBox, avgValue] = pill('Moyenne', '--');
    const hud = h('div', 'ac-hud');
    hud.append(roundBox, lastBox, avgBox);

    let state: 'idle' | 'waiting' | 'ready' = 'idle';
    let greenAt = 0;
    let round = 0;
    const results: number[] = [];

    const setIdle = (text: string): void => {
      state = 'idle';
      zone.style.background = '#241539';
      zone.textContent = text;
    };

    const arm = (): void => {
      state = 'waiting';
      zone.style.background = '#7f1d1d';
      zone.textContent = 'Attends le vert';
      // A random delay, or the whole game becomes muscle memory on a metronome.
      life.timeout(
        () => {
          if (state !== 'waiting') return;
          state = 'ready';
          greenAt = performance.now();
          zone.style.background = '#166534';
          zone.textContent = 'MAINTENANT';
          tone(880, 60, 'triangle');
        },
        900 + Math.random() * 2600,
      );
    };

    const finish = (): void => {
      const best = Math.min(...results);
      ctx.submit(best);
      setIdle(`Termine. Meilleure manche ${best} ms. Touche pour recommencer.`);
      round = 0;
      results.length = 0;
    };

    life.on(zone, 'pointerdown', (ev: Event) => {
      ev.preventDefault();
      if (state === 'idle') {
        round = round || 0;
        round++;
        roundValue.textContent = `${Math.min(round, 5)} / 5`;
        arm();
        return;
      }
      if (state === 'waiting') {
        // Too early. Nothing is recorded, or the score would reward guessing.
        tone(160, 200, 'square');
        setIdle('Trop tot. Touche pour reprendre cette manche.');
        round = Math.max(0, round - 1);
        roundValue.textContent = `${Math.min(round + 1, 5)} / 5`;
        return;
      }
      const ms = Math.round(performance.now() - greenAt);
      results.push(ms);
      lastValue.textContent = `${ms} ms`;
      avgValue.textContent = `${Math.round(results.reduce((a, b) => a + b, 0) / results.length)} ms`;
      tone(660, 70);
      if (results.length >= 5) {
        finish();
        return;
      }
      setIdle('Touche pour la manche suivante');
    });

    setIdle('Touche pour commencer');
    root.append(hud, zone, note);
    return () => life.stop();
  },
};

// --------------------------------------------------------------- click speed

export const clickSpeed: GameDef = {
  id: 'click-speed',
  title: 'Vitesse de clic',
  blurb: 'Combien de clics en dix secondes.',
  category: 'Réflexe',
  difficulty: 'Facile',
  icon: '\u{1F5B1}',
  unit: 'clics',
  higherIsBetter: true,
  how: 'Dix secondes, autant de clics que possible. Le chrono demarre au premier clic.',
  mount(root, ctx) {
    const life = new Lifecycle();
    const [timeBox, timeValue] = pill('Temps', '10.0');
    const [clickBox, clickValue] = pill('Clics', '0');
    const [cpsBox, cpsValue] = pill('Par seconde', '0.0');
    const hud = h('div', 'ac-hud');
    hud.append(timeBox, clickBox, cpsBox);

    const button = h('button', 'ac-primary', 'Clique ici');
    button.style.cssText =
      'width:100%;max-width:460px;height:220px;font-size:26px;border-radius:18px';

    let running = false;
    let clicks = 0;
    let startedAt = 0;

    const finish = (): void => {
      running = false;
      button.textContent = `${clicks} clics. Rejouer`;
      ctx.submit(clicks);
    };

    life.on(button, 'pointerdown', (ev: Event) => {
      ev.preventDefault();
      if (!running) {
        running = true;
        clicks = 0;
        startedAt = performance.now();
        button.textContent = 'Vas y';
      }
      clicks++;
      clickValue.textContent = String(clicks);
      tone(520 + (clicks % 5) * 40, 25, 'square', 0.025);
    });

    life.frame(() => {
      if (!running) return;
      const left = Math.max(0, 10 - (performance.now() - startedAt) / 1000);
      timeValue.textContent = left.toFixed(1);
      const spent = 10 - left;
      cpsValue.textContent = spent > 0.2 ? (clicks / spent).toFixed(1) : '0.0';
      if (left <= 0) finish();
    });

    root.append(hud, button, h('p', 'ac-note', 'Le maintien ne compte pas, il faut relacher.'));
    return () => life.stop();
  },
};

// -------------------------------------------------------------- aim trainer

export const aimTrainer: GameDef = {
  id: 'aim-trainer',
  title: 'Aim Trainer',
  blurb: 'Touche les cibles avant qu’elles ne disparaissent.',
  category: 'Réflexe',
  difficulty: 'Moyen',
  icon: '\u{1F3AF}',
  unit: 'points',
  higherIsBetter: true,
  how: 'Trente secondes. Chaque cible touchee vaut des points, et plus vite tu la touches plus elle rapporte.',
  mount(root, ctx) {
    const life = new Lifecycle();
    const [timeBox, timeValue] = pill('Temps', '30.0');
    const [scoreBox, scoreValue] = pill('Score', '0');
    const [hitBox, hitValue] = pill('Precision', '--');
    const hud = h('div', 'ac-hud');
    hud.append(timeBox, scoreBox, hitBox);

    const canvas = h('canvas', 'ac-canvas wide') as HTMLCanvasElement;
    const { w, h: hh, ctx: g } = fitCanvas(canvas, life);

    let running = false;
    let score = 0;
    let shots = 0;
    let hits = 0;
    let endsAt = 0;
    /*
     * Difficulty is how big the targets are and how long they stay.
     *
     * Both, because either alone is easy to cheat: huge targets that vanish
     * instantly reward spraying, and tiny ones that wait forever reward
     * nothing at all. The scoring already pays more for small and fast, so the
     * bonus curve is left alone and only the spawn changes.
     */
    const R_MIN = byLevel(ctx.level, 30, 20, 14);
    const R_SPREAD = byLevel(ctx.level, 22, 18, 12);
    const LIFE = byLevel(ctx.level, 2.2, 1.5, 1.05);
    const LIFE_FLOOR = byLevel(ctx.level, 0.9, 0.55, 0.4);
    const LIFE_DECAY = byLevel(ctx.level, 0.018, 0.028, 0.038);

    let target = { x: 0, y: 0, r: 34, born: 0, life: LIFE };

    const spawn = (): void => {
      const r = R_MIN + Math.random() * R_SPREAD;
      target = {
        x: r + Math.random() * (w() - r * 2),
        y: r + Math.random() * (hh() - r * 2),
        r,
        born: performance.now(),
        // Targets get shorter lived as the run goes on, so a good player is
        // pushed rather than left to grind an easy pace.
        life: Math.max(LIFE_FLOOR, LIFE - (30 - (endsAt - performance.now()) / 1000) * LIFE_DECAY),
      };
    };

    const start = (): void => {
      running = true;
      score = 0;
      shots = 0;
      hits = 0;
      endsAt = performance.now() + 30_000;
      spawn();
    };

    life.on(canvas, 'pointerdown', (ev: Event) => {
      const e = ev as PointerEvent;
      e.preventDefault();
      if (!running) {
        start();
        return;
      }
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      shots++;
      const d = Math.hypot(x - target.x, y - target.y);
      if (d <= target.r) {
        hits++;
        const age = (performance.now() - target.born) / 1000;
        // Reward speed and small targets, both of which are the actual skill.
        const speedBonus = Math.max(0, 1 - age / target.life);
        const sizeBonus = (40 - target.r) / 20;
        score += Math.round(10 + speedBonus * 20 + sizeBonus * 8);
        tone(760, 45, 'triangle');
        spawn();
      } else {
        tone(180, 70, 'square', 0.03);
      }
      scoreValue.textContent = String(score);
      hitValue.textContent = `${Math.round((hits / shots) * 100)} %`;
    });

    life.frame(() => {
      g.clearRect(0, 0, w(), hh());
      g.fillStyle = '#05030b';
      g.fillRect(0, 0, w(), hh());

      if (!running) {
        g.fillStyle = '#a394c8';
        g.font = '600 18px system-ui, sans-serif';
        g.textAlign = 'center';
        g.fillText('Touche pour commencer', w() / 2, hh() / 2);
        return;
      }

      const left = Math.max(0, (endsAt - performance.now()) / 1000);
      timeValue.textContent = left.toFixed(1);
      if (left <= 0) {
        running = false;
        ctx.submit(score);
        return;
      }

      const age = (performance.now() - target.born) / 1000;
      if (age > target.life) {
        // A missed target costs accuracy, so hiding from hard shots is not free.
        shots++;
        hitValue.textContent = `${Math.round((hits / shots) * 100)} %`;
        spawn();
      }

      const remain = Math.max(0, 1 - age / target.life);
      g.beginPath();
      g.arc(target.x, target.y, target.r, 0, Math.PI * 2);
      g.fillStyle = `rgba(255, 45, 149, ${0.25 + remain * 0.5})`;
      g.fill();
      g.lineWidth = 3;
      g.strokeStyle = '#22d3ee';
      g.stroke();
      // A shrinking ring, so the time left on a target is readable at a glance.
      g.beginPath();
      g.arc(target.x, target.y, target.r + 8, -Math.PI / 2, -Math.PI / 2 + remain * Math.PI * 2);
      g.strokeStyle = '#facc15';
      g.lineWidth = 3;
      g.stroke();
    });

    root.append(hud, canvas, h('p', 'ac-note', 'Les cibles retrecissent et durent moins longtemps.'));
    return () => life.stop();
  },
};

// -------------------------------------------------------------- colour match

const COLOURS = [
  { name: 'ROUGE', css: '#ef4444' },
  { name: 'VERT', css: '#22c55e' },
  { name: 'BLEU', css: '#3b82f6' },
  { name: 'JAUNE', css: '#facc15' },
  { name: 'VIOLET', css: '#a855f7' },
  { name: 'ORANGE', css: '#fb923c' },
];

export const colorMatch: GameDef = {
  id: 'color-match',
  title: 'Color Match',
  blurb: 'Le mot correspond il a sa couleur.',
  category: 'Réflexe',
  difficulty: 'Moyen',
  icon: '\u{1F308}',
  unit: 'points',
  higherIsBetter: true,
  how: 'Reponds si le MOT affiche correspond a la COULEUR dans laquelle il est ecrit. Trois erreurs et c’est fini.',
  mount(root, ctx) {
    const life = new Lifecycle();
    /*
     * Difficulty is how long you get to answer, and how many mistakes you may
     * make. The Stroop effect itself does not have a dial: the interference
     * between reading a word and naming a colour is either there or it is not.
     * What a difficulty can honestly change is the clock and the margin.
     */
    const START_MS = byLevel(ctx.level, 4200, 3000, 2200);
    const SQUEEZE = byLevel(ctx.level, 30, 45, 62);
    const FLOOR_MS = byLevel(ctx.level, 1300, 900, 620);
    const START_LIVES = byLevel(ctx.level, 5, 3, 2);

    const [scoreBox, scoreValue] = pill('Score', '0');
    const [livesBox, livesValue] = pill('Vies', '\u2665'.repeat(START_LIVES));
    const [timeBox, timeValue] = pill('Temps', (START_MS / 1000).toFixed(1));
    const hud = h('div', 'ac-hud');
    hud.append(scoreBox, livesBox, timeBox);

    const word = h('p', 'ac-big', '');
    word.style.cssText = 'font-size:clamp(40px,12vw,84px);letter-spacing:2px;margin:26px 0';

    const buttons = h('div');
    buttons.style.cssText = 'display:flex;gap:12px;flex-wrap:wrap;justify-content:center';
    const yes = h('button', 'ac-primary', 'Oui');
    const no = h('button', 'ac-ghost', 'Non');
    no.style.cssText = 'padding:12px 22px;font-size:15px';
    buttons.append(yes, no);

    let score = 0;
    let lives = START_LIVES;
    let matching = false;
    let deadline = 0;
    let running = true;

    const next = (): void => {
      const label = randomOf(COLOURS);
      // Half the rounds match, so guessing one answer forever averages zero.
      matching = Math.random() < 0.5;
      const ink = matching ? label : randomOf(COLOURS.filter((c) => c.name !== label.name));
      word.textContent = label.name;
      word.style.color = ink.css;
      // The window shrinks with the score: the pressure IS the difficulty.
      deadline = performance.now() + Math.max(FLOOR_MS, START_MS - score * SQUEEZE);
    };

    const wrong = (): void => {
      lives--;
      livesValue.textContent = '♥'.repeat(Math.max(0, lives)) || '--';
      tone(170, 200, 'square');
      if (lives <= 0) {
        running = false;
        ctx.submit(score);
        word.textContent = 'FINI';
        word.style.color = '#a394c8';
        yes.disabled = true;
        no.disabled = true;
        const again = h('button', 'ac-primary', 'Rejouer');
        again.addEventListener('click', () => ctx.exit());
        return;
      }
      next();
    };

    const answer = (said: boolean): void => {
      if (!running) return;
      if (said === matching) {
        score++;
        scoreValue.textContent = String(score);
        tone(680, 50, 'triangle');
        next();
      } else {
        wrong();
      }
    };

    life.on(yes, 'click', () => answer(true));
    life.on(no, 'click', () => answer(false));
    life.on(window, 'keydown', (ev: KeyboardEvent) => {
      if (ev.key === 'ArrowLeft') answer(true);
      if (ev.key === 'ArrowRight') answer(false);
    });

    life.frame(() => {
      if (!running) return;
      const left = Math.max(0, (deadline - performance.now()) / 1000);
      timeValue.textContent = left.toFixed(1);
      if (left <= 0) wrong();
    });

    next();
    root.append(
      hud,
      word,
      buttons,
      h('p', 'ac-note', 'Au clavier: fleche gauche pour oui, fleche droite pour non.'),
    );
    return () => life.stop();
  },
};
